import { useLocation, useNavigate } from "react-router-dom";
import { Home, Search, Heart, ShoppingCart, User } from "lucide-react";
import { useCart } from "@/hooks/useCart";
import { useAuth } from "@/hooks/useAuth";
import { useFavorites } from "@/hooks/useFavorites";
import { useStoreUrl } from "@/hooks/useStoreUrl";

export default function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { itemCount } = useCart();
  const { user } = useAuth();
  const { count: favCount } = useFavorites();
  const { storeUrl, basePath } = useStoreUrl();

  const NAV_ITEMS = [
    { path: storeUrl("/"), icon: Home, label: "Início", matchExact: true },
    { path: storeUrl("/catalog"), icon: Search, label: "Buscar" },
    { path: storeUrl("/favorites"), icon: Heart, label: "Favoritos" },
    { path: storeUrl("/cart"), icon: ShoppingCart, label: "Carrinho" },
    { path: storeUrl("/account"), icon: User, label: "Conta" },
  ];

  const handleNav = (path: string) => {
    if ((path.includes("/account") || path.includes("/favorites")) && !user) {
      navigate(storeUrl("/auth"));
      return;
    }
    navigate(path);
  };

  // Hide on admin and auth routes
  if (location.pathname.startsWith("/admin") || location.pathname.endsWith("/auth")) return null;

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 md:hidden border-t border-border/60 bg-card/95 backdrop-blur-lg supports-[backdrop-filter]:bg-card/85 safe-area-bottom">
      <div className="flex items-center justify-around h-14 px-1 max-w-screen-lg mx-auto">
        {NAV_ITEMS.map(({ path, icon: Icon, label, matchExact }) => {
          const isActive = matchExact
            ? location.pathname === path
            : location.pathname.startsWith(path);

          return (
            <button
              key={path}
              onClick={() => handleNav(path)}
              className={`relative flex flex-col items-center justify-center gap-0.5 flex-1 h-full transition-colors ${
                isActive
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground active:text-foreground"
              }`}
            >
              <div className="relative">
                <Icon
                  className={`h-5 w-5 transition-transform ${isActive ? "scale-110" : ""}`}
                  strokeWidth={isActive ? 2.5 : 2}
                />
                {path.includes("/cart") && itemCount > 0 && (
                  <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 rounded-full bg-destructive text-destructive-foreground text-[9px] font-bold flex items-center justify-center px-1 shadow-sm">
                    {itemCount > 99 ? "99+" : itemCount}
                  </span>
                )}
                {path.includes("/favorites") && favCount > 0 && (
                  <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 rounded-full bg-destructive text-destructive-foreground text-[9px] font-bold flex items-center justify-center px-1 shadow-sm">
                    {favCount > 9 ? "9+" : favCount}
                  </span>
                )}
              </div>
              <span
                className={`text-[10px] leading-tight ${
                  isActive ? "font-semibold" : "font-medium"
                }`}
              >
                {label}
              </span>
              {isActive && (
                <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full bg-primary" />
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
